/**
 * The checks a review line applies, on the line's own page (simplification,
 * 2026-09-24): lenses inside the one review, never a second reviewer.
 *
 * One row per check: its name, what it looks for, and at the right edge its
 * switch, or "always on" for the two core checks the review never runs
 * without. A check's instructions fold behind a small link and open inline,
 * one check at a time: a built-in takes this Team's additions on top of the
 * platform's own text (which never reaches a client), a check the Team wrote
 * shows its whole text and can be retired. "Add a check" folds behind a plus:
 * a name, what it looks for, and optionally the paths it applies to.
 *
 * Proctor's review checks and Reeve's watch-set are the same roster in two
 * voices (`checksCopyFor`). Two write targets, kept apart on purpose:
 *
 *   - the SWITCH edits the line's own off-list, through the page's draft, so
 *     it saves with the line;
 *   - INSTRUCTIONS are the Team's (`desk_specialist_customizations`,
 *     `desk_playbook_roles`): an edit is held as a draft the page's Save
 *     writes (`saveCheckDrafts`), and adding or retiring a check lands at
 *     once, because it changes the roster itself.
 */

import { useCallback, useEffect, useState } from "react";

import { BrandLoader } from "../ui/brand-loader.js";
import {
  CHECKS_ADD,
  CHECKS_ADD_NAME,
  CHECKS_ADD_ONLY_ON,
  CHECKS_ADD_ONLY_ON_PLACEHOLDER,
  CHECKS_ALWAYS_ON,
  CHECKS_ERROR,
  CHECKS_INSTRUCTIONS_ADD,
  CHECKS_INSTRUCTIONS_HIDE,
  CHECKS_INSTRUCTIONS_OPEN,
  CHECKS_LOADING,
  CHECKS_NAME_UNSLUGGABLE,
  CHECKS_RETIRE,
  CHECKS_RETIRE_CONFIRM,
  CHECKS_RETRY,
  CHECKS_SAVE_FAILED,
  CHECKS_SLUG_TAKEN,
  CHECKS_YOURS,
  checksCopyFor,
} from "./copy.js";

export interface CheckRow {
  id: string;
  slug: string;
  name: string;
  workflow: string;
  /** Custom checks only: the Team's own text. Built-ins never send theirs. */
  instructions?: string;
  /** Built-ins only: one line on what the check looks for. */
  overview?: string;
  /** Built-ins only: this Team's additions, when any. */
  extraInstructions?: string;
  activation: { always?: boolean; paths?: string[]; [key: string]: unknown };
  provenance: "platform" | "workspace";
  /** A core check the review never runs without. */
  alwaysOn: boolean;
}

/** An instruction edit the page's Save writes, keyed by the check's id. */
export interface CheckDraft {
  row: CheckRow;
  text: string;
}

export type CheckDrafts = Record<string, CheckDraft>;

/** The name to slug derivation the server applies, run first so a punctuation-only name is refused here. */
function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function parseRows(body: unknown): CheckRow[] {
  const roles = (body as { roles?: unknown } | null)?.roles;
  if (!Array.isArray(roles)) return [];
  return roles.filter(
    (row): row is CheckRow =>
      !!row &&
      typeof row === "object" &&
      typeof (row as CheckRow).slug === "string" &&
      typeof (row as CheckRow).name === "string",
  );
}

/** What a row's instructions read as now: the Team's own words, never the platform's. */
function storedText(row: CheckRow): string {
  return row.provenance === "platform" ? (row.extraInstructions ?? "") : (row.instructions ?? "");
}

/**
 * Whether Save would write this draft: its text differs from what the check
 * holds now, and a Team's own check is never written empty (retire it instead).
 */
export function checkDraftChanged({ row, text }: CheckDraft): boolean {
  const next = text.trim();
  if (next === storedText(row).trim()) return false;
  return row.provenance === "platform" || next !== "";
}

async function readError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? CHECKS_SAVE_FAILED;
}

/**
 * Write the held instruction edits. A built-in's additions are replaced, or
 * removed when emptied; a Team's own check has its text replaced (it cannot
 * be emptied: retire it instead). Resolves to the first refusal, or null.
 */
export async function saveCheckDrafts(
  deskId: string,
  drafts: CheckDrafts,
): Promise<string | null> {
  const base = `/api/desks/${encodeURIComponent(deskId)}`;
  for (const draft of Object.values(drafts)) {
    if (!checkDraftChanged(draft)) continue;
    const { row } = draft;
    const next = draft.text.trim();
    let res: Response;
    if (row.provenance === "platform") {
      const path = `${base}/specialists/${encodeURIComponent(row.slug)}/instructions`;
      res = next
        ? await fetch(path, {
            method: "PUT",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ workflow: row.workflow, extraInstructions: next }),
          })
        : await fetch(`${path}?workflow=${encodeURIComponent(row.workflow)}`, {
            method: "DELETE",
            credentials: "include",
          });
    } else {
      res = await fetch(`${base}/playbook-roles/${encodeURIComponent(row.id)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ instructions: next }),
      });
    }
    if (!res.ok) return readError(res);
  }
  return null;
}

export interface ChecksRosterProps {
  deskId: string;
  /** The specialist workflow this line's checks come from: `code_review` or `plan_review`. */
  workflow: string;
  /** The slugs the line has switched off (its `checksOff` / `disabledRoles`). */
  off: readonly string[];
  disabled: boolean;
  onOffChange: (off: string[]) => void;
  drafts: CheckDrafts;
  onDraftsChange: (drafts: CheckDrafts) => void;
}

export function ChecksRoster({
  deskId,
  workflow,
  off,
  disabled,
  onOffChange,
  drafts,
  onDraftsChange,
}: ChecksRosterProps) {
  const copy = checksCopyFor(workflow);
  const [rows, setRows] = useState<CheckRow[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  /** The one check whose instructions are open. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newText, setNewText] = useState("");
  const [newPaths, setNewPaths] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(
        `/api/desks/${encodeURIComponent(deskId)}/playbook-roles?workflow=${encodeURIComponent(workflow)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(String(res.status));
      setRows(parseRows(await res.json()));
      setLoadError(false);
    } catch {
      // A refresh that fails keeps the roster on screen; a first read that
      // fails says so, because silence would read as "no checks".
      setRows((prev) => {
        if (prev === null) setLoadError(true);
        return prev;
      });
    }
  }, [deskId, workflow]);

  useEffect(() => {
    void load();
  }, [load]);

  const setEnabled = (slug: string, enabled: boolean) => {
    const next = off.filter((entry) => entry !== slug);
    onOffChange(enabled ? next : [...next, slug]);
  };

  const add = async () => {
    const slug = slugify(newName);
    if (!slug) {
      setError(CHECKS_NAME_UNSLUGGABLE);
      return;
    }
    const paths = newPaths
      .split(/[,\n]/)
      .map((entry) => entry.trim())
      .filter(Boolean);
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/desks/${encodeURIComponent(deskId)}/playbook-roles`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          workflow,
          slug,
          name: newName.trim(),
          instructions: newText.trim(),
          // No paths means every review: a lens a person adds is one they
          // want applied, not one the reviewer may skip.
          activation: paths.length > 0 ? { paths } : { always: true },
        }),
      });
      if (!res.ok) {
        setError(res.status === 409 ? CHECKS_SLUG_TAKEN : await readError(res));
        return;
      }
      setNewName("");
      setNewText("");
      setNewPaths("");
      setAdding(false);
      await load();
    } catch {
      setError(CHECKS_SAVE_FAILED);
    } finally {
      setBusy(false);
    }
  };

  const retire = async (row: CheckRow) => {
    if (!window.confirm(CHECKS_RETIRE_CONFIRM)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/desks/${encodeURIComponent(deskId)}/playbook-roles/${encodeURIComponent(row.id)}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!res.ok) {
        setError(await readError(res));
        return;
      }
      // A retired check leaves the off-list too, and takes its draft with it.
      if (off.includes(row.slug)) onOffChange(off.filter((entry) => entry !== row.slug));
      onDraftsChange(
        Object.fromEntries(Object.entries(drafts).filter(([id]) => id !== row.id)),
      );
      setOpenId(null);
      await load();
    } catch {
      setError(CHECKS_SAVE_FAILED);
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <p className="fact" data-testid="team-checks-error">
        {CHECKS_ERROR}{" "}
        <button type="button" className="ref quiet" onClick={() => void load()}>
          {CHECKS_RETRY}
        </button>
      </p>
    );
  }
  if (rows === null) {
    return <BrandLoader size="sm" label={CHECKS_LOADING} data-testid="team-checks-loading" />;
  }

  return (
    <div className="checklist" data-testid="team-checks">
      {rows.map((row) => {
        const enabled = row.alwaysOn || !off.includes(row.slug);
        const text = drafts[row.id]?.text ?? storedText(row);
        const custom = row.provenance === "workspace";
        const open = openId === row.id;
        const linkLabel = open ? CHECKS_INSTRUCTIONS_HIDE : text.trim() ? CHECKS_INSTRUCTIONS_OPEN : CHECKS_INSTRUCTIONS_ADD;
        return (
          <div key={row.id} className="chk" data-testid={`team-check-${row.slug}`}>
            <label>
              <input
                type="checkbox"
                checked={enabled}
                disabled={disabled || row.alwaysOn}
                aria-label={`${row.name}: ${enabled ? "on" : "off"}`}
                data-testid={`team-check-toggle-${row.slug}`}
                onChange={(event) => setEnabled(row.slug, event.target.checked)}
              />
              <span>
                <b style={{ fontWeight: 600 }}>{row.name}</b>
                {row.alwaysOn && (
                  <>
                    {" "}
                    <span className="fact" style={{ fontSize: 10.5 }}>
                      {CHECKS_ALWAYS_ON}
                    </span>
                  </>
                )}
                <span className="d">
                  {" · "}
                  {custom ? `${CHECKS_YOURS} · ${firstLine(row.instructions ?? "")}` : (row.overview ?? "")}
                </span>
              </span>
            </label>
            {!row.alwaysOn && (
              <button
                type="button"
                className="ref"
                aria-expanded={open}
                data-testid={`team-check-instructions-${row.slug}`}
                onClick={() => setOpenId(open ? null : row.id)}
              >
                {linkLabel}
              </button>
            )}
            {open && (
              <>
                <textarea
                  className="chkin"
                  rows={3}
                  maxLength={4000}
                  value={text}
                  disabled={disabled}
                  aria-label={`${row.name} instructions`}
                  placeholder={custom ? copy.instructionsPlaceholder : copy.deltaPlaceholder}
                  data-testid={`team-check-text-${row.slug}`}
                  onChange={(event) => onDraftsChange({ ...drafts, [row.id]: { row, text: event.target.value } })}
                />
                {custom && (
                  <button
                    type="button"
                    className="ref quiet"
                    style={{ marginLeft: 31, alignSelf: "flex-start", color: "var(--incident)" }}
                    disabled={disabled || busy}
                    data-testid={`team-check-retire-${row.slug}`}
                    onClick={() => void retire(row)}
                  >
                    {CHECKS_RETIRE}
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}

      {adding ? (
        <div className="addform" data-testid="team-check-add-form">
          <div className="field">
            <label htmlFor={`${workflow}-check-name`}>{CHECKS_ADD_NAME}</label>
            <div className="in">
              <input
                id={`${workflow}-check-name`}
                maxLength={100}
                value={newName}
                placeholder={copy.namePlaceholder}
                data-testid="team-check-name"
                onChange={(event) => setNewName(event.target.value)}
              />
            </div>
          </div>
          <div className="field top">
            <label htmlFor={`${workflow}-check-what`}>{copy.whatLabel}</label>
            <textarea
              id={`${workflow}-check-what`}
              rows={2}
              maxLength={4000}
              value={newText}
              placeholder={copy.instructionsPlaceholder}
              data-testid="team-check-what"
              onChange={(event) => setNewText(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor={`${workflow}-check-paths`}>{CHECKS_ADD_ONLY_ON}</label>
            <div className="in mono">
              <input
                id={`${workflow}-check-paths`}
                value={newPaths}
                placeholder={CHECKS_ADD_ONLY_ON_PLACEHOLDER}
                data-testid="team-check-paths"
                onChange={(event) => setNewPaths(event.target.value)}
              />
            </div>
          </div>
          <div className="acts" style={{ border: 0, paddingTop: 0 }}>
            <button
              type="button"
              className="btn quiet sm"
              disabled={busy}
              onClick={() => {
                setAdding(false);
                setError(null);
              }}
            >
              {copy.cancel}
            </button>
            <button
              type="button"
              className="btn secondary sm"
              disabled={disabled || busy || !newName.trim() || !newText.trim()}
              data-testid="team-check-add"
              onClick={() => void add()}
            >
              {CHECKS_ADD}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="chip add"
          style={{ margin: "4px 8px", alignSelf: "flex-start", border: "1px dashed var(--dash-border)", background: "none" }}
          disabled={disabled}
          data-testid="team-check-new"
          onClick={() => setAdding(true)}
        >
          + {copy.newButton}
        </button>
      )}

      {error && (
        <p className="fact" role="alert" style={{ color: "var(--incident)" }} data-testid="team-checks-message">
          {error}
        </p>
      )}
    </div>
  );
}

function firstLine(text: string): string {
  const line = text.split("\n")[0]?.trim() ?? "";
  return line.length > 80 ? `${line.slice(0, 79)}…` : line;
}
